import { mapActions } from 'pinia';
import useSelectionStore from '@/stores/selection';
import useUiPlanningStore from '@/stores/ui/planning';
import usePlanningElementsStore from '@/stores/planning/elements';
import constants from '@/js/constants.js';
import PlanningElement from '@/models/PlanningElement';

let lastPlaceholderMove = 0;
let livedragThrottledTimeout;
let dragClone = null;

function createDragClone(event, cursorOffset) {
  dragClone = event.target.cloneNode(true);
  dragClone.querySelector('.element-menu')?.remove();
  dragClone.style.position = 'fixed';
  dragClone.style.pointerEvents = 'none';
  dragClone.style.zIndex = 9999;
  dragClone.style.opacity = 0.8;
  document.body.appendChild(dragClone);
  dragClone.style.left = `${event.pageX - window.scrollX - cursorOffset.x}px`;
  dragClone.style.top = `${event.pageY - window.scrollY - cursorOffset.y}px`;
}

function movePlaceholder(planning, target) {
  lastPlaceholderMove = Date.now();
  const dragData = target.dragData || {};
  const placeholderEl = planning.elements.find(el => el.id == dragData.placeholderId);
  if (! placeholderEl) return;
  const positionX = dragData.position.left - dragData.cursorOffset.x - dragData.laneXOffset;
  if (dragData.mode == 'kanban') {
    const referenceXPosition = positionX + dragData.cloneDimensions.width / 2;
    const referenceYPosition = dragData.position.top + dragData.cloneDimensions.height / 2;
    placeholderEl.kanbanMove(placeholderEl.getLaneId(), referenceXPosition, referenceYPosition); // x relative to lane, y relative to page
  } else {
    placeholderEl.move(placeholderEl.getLaneId(), positionX); // x relative to lane
  }
}

const livedragThrottled = _.throttle((planning, target) => {
  if (livedragThrottledTimeout) clearTimeout(livedragThrottledTimeout);
  if (Date.now() - lastPlaceholderMove < 3000) {
    livedragThrottledTimeout = setTimeout(() => movePlaceholder(planning, target));
    return;
  }
  movePlaceholder(planning, target);
}, 150);

export default {
  data() {
    return {
      selectionStore: useSelectionStore(),
      uiPlanningStore: useUiPlanningStore(),
      planningElementsStore: usePlanningElementsStore(),
    };
  },
  computed: {
    selection() { return this.selectionStore.selection; },
  },
  methods: {
    draggableConfig(el) {
      if (! this.isDynamic(el)) return { enabled: false };
      const vm = this;
      return {
        enabled: ! (el.isType('macro') || el.getIsLocked() || (this.planning.config.lockDragAndDrop && this.planning.meta.access_right != 'admin')),
        cursorChecker() { return null; },
        ignoreFrom: '.element-menu',
        listeners: {
          start(event) {
            vm.dragStart(event, el);

            const livedragActive = vm.selection.size <= 1;
            if (livedragActive) { // only when dragging 1 element
              vm.livedragStart(event, el);
            } else {
              setTimeout(() => setTimeout(() => $(`#el${el.id}`).css('left', '-1000px')));
            }
          },
          move(event) {
            vm.dragSelection(event, el);
          },
          end(event) {
            if (dragClone) {
              dragClone.remove();
              dragClone = null;
            }
            vm.uiPlanningStore.doNotDisplayElementMenu = false;
            setTimeout(() => { event.target.classList.remove('is-dragging'); }, 300);
            vm.removeDragPlaceholders(event, el);
            if (event.target.dragData.selection.size) {
              vm.planning.elements.forEach((selectedEl) => { // force refresh of css prop changed by dragging (in case of dropping outside dropzone)
                if (event.target.dragData.selection.has(selectedEl.id)) selectedEl.update();
              });
            }
            vm.uiPlanningStore.doNotUpdateSelection = false;
          },
        },
      };
    },

    dragStart(event, el) {
      const rect = event.target.getBoundingClientRect();
      const cursorOffset = { // position of cursor in element when drag starts
        x: event.pageX - (rect.left + window.scrollX),
        y: event.pageY - (rect.top + window.scrollY),
      };
      createDragClone(event, cursorOffset);

      const cloneRect = dragClone.getBoundingClientRect();

      event.target.dragData = { // store data in event.target
        id: el.id,
        laneXOffset: document.getElementById(`lane${el.getLaneId()}`)?.getBoundingClientRect().left || 0, // to calculate position in lane
        cursorOffset, // to position the clone correctly
        position: { left: event.pageX, top: event.pageY }, // current position in page
        uiDragPrevPosition: { left: event.pageX, top: event.pageY },
        cloneDimensions: { width: cloneRect.width, height: cloneRect.height },
      };

      this.planningElementsStore.startChangingElement();
      this.uiPlanningStore.doNotDisplayElementMenu = true;
      event.target.classList.add('is-dragging');

      const notLockedSelection = [];
      this.planning.elements.forEach((item) => {
        if (this.selection.has(item.id) && ! item.getIsLocked()) notLockedSelection.push(item.id);
      });
      if (notLockedSelection.length != this.selection.size) this.selectionStore.reset(notLockedSelection);
      if (! this.selection.has(el.id)) this.selectionStore.reset([el.id]);
      event.target.dragData.selection = new Set(this.selection);
      if (el.isType('task')) this.setSnapBoundaries(event.target);

      this.removeDragPlaceholders();

      el.visible = false; // hide original element
    },

    livedragStart(event, el) {
      event.target.dragData.livedragActive = true;
      const placeholderEl = new PlanningElement(this.planning, el); // create placeholder synchronously
      placeholderEl.setDependencies(null);
      placeholderEl.id = this.planningElementsStore.nextId;
      this.planningElementsStore.add(placeholderEl);
      placeholderEl.isDragPlaceholder = true;
      event.target.dragData.placeholderId = placeholderEl.id;
      return placeholderEl;
    },

    dragSelection(event, el) {
      event.target.dragData.position = { left: event.pageX, top: event.pageY };
      if (dragClone) {
        dragClone.style.left = `${event.pageX - window.scrollX - event.target.dragData.cursorOffset.x}px`;
        dragClone.style.top = `${event.pageY - window.scrollY - event.target.dragData.cursorOffset.y}px`;
      }

      if (el.isType('task')) this.snapToBoundaries(event.target);

      if (event.target.dragData.livedragActive) {
        livedragThrottled(this.planning, event.target);
      }

      if (this.selection.size <= 1) return;

      // selection drag
      const prevPosition = event.target.dragData.uiDragPrevPosition;
      const newPosition = event.target.dragData.position;
      const offsetleft = newPosition.left - prevPosition.left;
      const offsettop = newPosition.top - prevPosition.top;

      this.selection.forEach((id) => {
        if (id == el.id) return;
        const $selectedElement = $(`#el${id}`);
        if ($selectedElement) {
          $selectedElement.css('left', `+=${offsetleft}`);
          $selectedElement.css('top', `+=${offsettop}`);
        }
      });
      if (event.target.dragData) event.target.dragData.uiDragPrevPosition = newPosition;
      this.uiPlanningStore.doNotUpdateSelection = true;
    },

    removeDragPlaceholders() {
      const dragPlaceholders = this.planning.elements.filter(item => item.isDragPlaceholder);
      if (dragPlaceholders.length) this.deleteElements({ els: dragPlaceholders, silent: true });
    },

    /** KANBAN */
    kanbanDraggableConfig(el) {
      if (! this.isDynamic(el)) return { enabled: false };
      const vm = this;
      return {
        enabled: ! (el.isType('macro') || el.getIsLocked() || (this.planning.config.lockDragAndDrop && this.planning.meta.access_right != 'admin')),
        cursorChecker() { return null; },
        ignoreFrom: '.element-menu',
        listeners: {
          start(event) {
            vm.dragStart(event, el);

            const livedragActive = vm.selection.size <= 1;
            if (livedragActive) { // only when dragging 1 element
              dragClone.style.width = `${constants.kanbanDragHelperWidth}px`; // set to smaller size to fit better in columns
              event.target.dragData.cloneDimensions.width = constants.kanbanDragHelperWidth;
              event.target.dragData.mode = 'kanban';
              const placeholderEl = vm.livedragStart(event, el);
              placeholderEl.setStartTime(null); // to appear when over backlog
              placeholderEl.setEndTime(null);

              event.target.dragData.originalStep = el.getProcessStep();
              el.setProcessStep('livedraghiding'); // hide original element
            } else {
              const $el = document.getElementById(`el${el.id}`);
              const { width } = $el.getBoundingClientRect();
              dragClone.style.width = `${width}px`; // keep original width for consistency with other dragged elements
              event.target.dragData.cloneDimensions.width = width;
              setTimeout(() => setTimeout(() => { $el.style.visibility = 'hidden'; })); // hide original element
            }
          },
          move(event) {
            vm.dragSelection(event, el);
          },
          end(event) {
            if (dragClone) {
              dragClone.remove();
              dragClone = null;
            }
            vm.uiPlanningStore.doNotDisplayElementMenu = false;
            setTimeout(() => { event.target.classList.remove('is-dragging'); }, 300);
            vm.removeDragPlaceholders(event, el);

            if (el.getProcessStep() == 'livedraghiding') el.setProcessStep(event.target.dragData.originalStep);

            if (event.target.dragData.selection.size) {
              vm.planning.elements.forEach((selectedEl) => { // force refresh of css prop changed by dragging (in case of dropping outside dropzone)
                if (event.target.dragData.selection.has(selectedEl.id)) $(`#el${selectedEl.id}`).css({ left: '', top: '', visibility: '' });
              });
            }
            vm.uiPlanningStore.doNotUpdateSelection = false;
          },
        },
      };
    },

    ...mapActions(usePlanningElementsStore, ['setSnapBoundaries', 'snapToBoundaries', 'deleteElements']),
  },
};
